import { create } from 'zustand'
import { reviewModeApi } from '@api/endpoints/reviewMode'
import { canUseReviewMode } from './reviewMode'

/**
 * Moderator review mode (Backlog 058).
 *
 * In memory only, deliberately: no persist middleware, so every page load starts with it off and
 * it can never be left on by accident. Read outside React (location reports, trail submits) with
 * `isReviewModeOn()`.
 *
 * Imports nothing from the auth store, which clears this one on login and logout; importing it
 * back would close a cycle through the location tracker.
 */
interface ReviewModeState {
  on: boolean
  /** A switch is being recorded with the server. */
  pending: boolean
  /** Why the last switch did not take, for the toggle to show. */
  error: string | null
  /** Sessions started with "Review (test play)" during this page load. */
  reviewSessionIds: string[]

  /**
   * Switch review mode. Resolves to whether it is now in the requested state.
   *
   * Turning ON takes effect immediately, so a location report racing the audit call already
   * carries the flag, and is undone if the server refuses or cannot record it: the audit is the
   * one thing review mode is supposed to leave behind. Turning OFF always takes effect; the audit
   * call is best effort.
   */
  setReviewMode: (on: boolean, roles: readonly string[] | null | undefined) => Promise<boolean>
  noteReviewSession: (sessionId: string) => void
  dismissError: () => void
  /** Local reset with no audit call (sign-in, sign-out: the session it belonged to is gone). */
  clear: () => void
}

const initial = { on: false, pending: false, error: null, reviewSessionIds: [] as string[] }

function statusOf(err: unknown): number | undefined {
  const e = err as { response?: { status?: number }; status?: number } | null
  return e?.response?.status ?? e?.status
}

export const useReviewModeStore = create<ReviewModeState>()((set, get) => ({
  ...initial,

  setReviewMode: async (on, roles) => {
    if (on && !canUseReviewMode(roles)) {
      set({ on: false, error: 'Review mode is for moderators.' })
      return false
    }
    if (get().on === on) return true

    set({ on, pending: true, error: null })
    try {
      await reviewModeApi.setReviewMode(on)
      set({ pending: false })
      return true
    } catch (err) {
      if (!on) {
        // Already off locally, which is the safe direction; a missing "off" audit row is all
        // that is lost.
        console.warn('[ReviewMode] Could not record leaving review mode', err)
        set({ pending: false })
        return true
      }
      set({
        on: false,
        pending: false,
        error:
          statusOf(err) === 403
            ? 'Review mode is for moderators.'
            : 'Could not turn on review mode. Try again.',
      })
      return false
    }
  },

  noteReviewSession: (sessionId) =>
    set((state) =>
      state.reviewSessionIds.includes(sessionId)
        ? state
        : { reviewSessionIds: [...state.reviewSessionIds, sessionId] }
    ),

  dismissError: () => set({ error: null }),

  clear: () => set({ ...initial, reviewSessionIds: [] }),
}))

/** For non-React callers: whether reports sent right now should carry `reviewMode: true`. */
export function isReviewModeOn(): boolean {
  return useReviewModeStore.getState().on
}
